import { Fragment, useMemo, useState } from 'react'
import {
  Box,
  Card,
  CardActionArea,
  Chip,
  CircularProgress,
  Link,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import type { SelectChangeEvent } from '@mui/material'
import { OpenInNew as OpenInNewIcon, Sensors as SensorsIcon } from '@mui/icons-material'

import type { JobOpportunity, SignalAction, WatchType } from '../../state/jobOpportunities'
import {
  SIGNAL_ACTIONS,
  SIGNAL_ACTION_LABELS,
  SIGNAL_TYPE_LABELS,
  WATCH_TYPES,
  WATCH_TYPE_LABELS,
  effectiveSignalAction,
} from '../../state/jobOpportunities'
import EmptyState from '../common/EmptyState'
import ErrorState from '../common/ErrorState'
import SortableTableHeader from '../common/table/SortableTableHeader'
import type { SortOrder } from '../common/table/SortableTableHeader'
import TablePaginationBar from '../common/table/TablePaginationBar'
import { useTablePagination } from '../common/table/useTablePagination'
import { ExpanderField, ExpanderHeaderCell, ExpanderRow, ExpanderToggleCell } from '../common/table/RowExpander'
import { getEnhancedTableStyles } from '../../utils/tableStyles'
import { getCompactTableSx } from '../../utils/compactTableStyles'
import { formatLocalYMD } from '../../utils/dateFormat'
import { signalEventLabel, signalHolderLabel, sortSignalRows, splitByWatchType } from './signalRows'
import type { SignalSortKey } from './signalRows'

interface SignalsTableProps {
  // The signals to show: hidden opportunities nobody has converted or archived
  // (isSignalJob). Both sub-tabs are cut from this one list.
  jobs: JobOpportunity[]
  // Staff and the portal read the same table and write the same Action; the
  // difference is the wording of the empty states.
  audience: 'staff' | 'client'
  // True only until the first read lands, so a refresh never blanks the table.
  loading: boolean
  // A failed read replaces both sub-tabs' tables rather than showing an empty
  // one, which would claim there is nothing to watch.
  error: string | null
  onRetry: () => void
  onOpen: (job: JobOpportunity) => void
  onActionChange: (job: JobOpportunity, action: SignalAction) => void
  // Id of the signal whose Action is being saved, so its select disables
  // rather than queueing a second write.
  savingActionId?: string | null
  // Which sub-tab is open. Held by the page so it can follow a newly logged
  // signal into the sub-tab it belongs to, and survive a reload in the URL.
  watchType: WatchType
  onWatchTypeChange: (watchType: WatchType) => void
}

const EMPTY_COPY: Record<WatchType, Record<'staff' | 'client', { title: string; description: string }>> = {
  signal: {
    staff: {
      title: 'No signal watches yet',
      description: 'When something happens at a company worth watching, log it from Log a Signal.',
    },
    client: {
      title: 'No signal watches yet',
      description: 'When we spot something happening at a company you want to work for, it will appear here.',
    },
  },
  seat: {
    staff: {
      title: 'No seat watches yet',
      description: 'When a seat looks like it is about to open, log it from Log a Signal.',
    },
    client: {
      title: 'No seat watches yet',
      description: 'When we spot a role opening up before it is advertised, it will appear here.',
    },
  },
}

// Company, Event, Role, Date, Action and the expander toggle. The other two
// columns of the seven are in the expander at tablet widths.
const COMPACT_COLUMN_COUNT = 6

/**
 * MWA-421 — the Signals page's two tables, Signal Watch and Seat Watch.
 *
 * Both sub-tabs show the same seven columns in the same order; the sub-tab only
 * decides which rows are in it. Built on the Front Door table's styling,
 * sorting and pagination so the two read as one family, and collapses the way
 * those do: tablet widths move two columns into a row expander, a phone gets
 * cards.
 *
 * The Action is a select on every row, for staff and client alike. Whether the
 * server accepts the write is decided there (update-job-opportunity); every
 * row here is a hidden opportunity nobody has converted, which is the window
 * the server allows a client to write in.
 */
export default function SignalsTable({
  jobs,
  audience,
  loading,
  error,
  onRetry,
  onOpen,
  onActionChange,
  savingActionId = null,
  watchType,
  onWatchTypeChange,
}: SignalsTableProps) {
  const theme = useTheme()
  const tableStyles = getEnhancedTableStyles(theme)
  const isPhone = useMediaQuery(theme.breakpoints.down(768))
  const isCompact = useMediaQuery(theme.breakpoints.down(1200))

  const [orderBy, setOrderBy] = useState<SignalSortKey>('date')
  const [order, setOrder] = useState<SortOrder>('desc')
  const [expandedRowId, setExpandedRowId] = useState<string | null>(null)

  const grouped = useMemo(() => splitByWatchType(jobs), [jobs])
  const rows = useMemo(
    () => sortSignalRows(grouped[watchType], orderBy, order),
    [grouped, watchType, orderBy, order]
  )
  const { paginated: pagedRows, ...pageCtl } = useTablePagination(rows, `${watchType}|${orderBy}|${order}`)

  const handleSort = (columnId: string) => {
    if (orderBy === columnId) {
      setOrder((o) => (o === 'asc' ? 'desc' : 'asc'))
    } else {
      setOrderBy(columnId as SignalSortKey)
      setOrder('asc')
    }
  }

  const renderEvent = (j: JobOpportunity) => (
    <Stack spacing={0.5} sx={{ alignItems: 'flex-start' }}>
      {j.signalType && (
        <Chip
          size="small"
          label={SIGNAL_TYPE_LABELS[j.signalType] || j.signalType}
          color="primary"
          variant="outlined"
          sx={{ height: 18, typography: 'badge' }}
        />
      )}
      <Typography variant="body2">{signalEventLabel(j) || '—'}</Typography>
    </Stack>
  )

  const renderLinkedIn = (j: JobOpportunity) =>
    j.companyLinkedInUrl ? (
      <Link
        href={j.companyLinkedInUrl}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => e.stopPropagation()}
        aria-label={`${j.company} on LinkedIn`}
        sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
      >
        LinkedIn
        <OpenInNewIcon sx={{ fontSize: 14 }} />
      </Link>
    ) : (
      <Typography variant="body2" color="text.secondary">
        —
      </Typography>
    )

  const renderHolder = (j: JobOpportunity) => {
    const label = signalHolderLabel(j)
    return label ? (
      <Typography variant="body2" sx={j.noPreviousHolder ? { fontStyle: 'italic' } : undefined}>
        {label}
      </Typography>
    ) : (
      <Typography variant="body2" color="text.secondary">
        —
      </Typography>
    )
  }

  const renderDate = (j: JobOpportunity) =>
    j.signalObservedDate ? formatLocalYMD(j.signalObservedDate, 'en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'

  const renderAction = (j: JobOpportunity) => {
    const busy = savingActionId === j.id
    return (
      // The row opens the detail dialog on click; the select and its menu (a
      // portal, whose events still bubble through the React tree to here) must
      // not.
      <Box
        sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}
        onClick={(e) => e.stopPropagation()}
      >
        <Select
          size="small"
          value={effectiveSignalAction(j)}
          disabled={busy}
          onChange={(e: SelectChangeEvent) => onActionChange(j, e.target.value as SignalAction)}
          inputProps={{ 'aria-label': `Action for ${j.jobTitle} at ${j.company}` }}
          sx={{ minWidth: 140, flex: 1 }}
        >
          {SIGNAL_ACTIONS.map((a) => (
            <MenuItem key={a} value={a}>
              {SIGNAL_ACTION_LABELS[a]}
            </MenuItem>
          ))}
        </Select>
        {busy && <CircularProgress size={16} />}
      </Box>
    )
  }

  const renderBody = () => {
    if (error) {
      return (
        <ErrorState
          title={`Couldn't load ${WATCH_TYPE_LABELS[watchType]}`}
          description="Nothing was changed, and retrying is safe."
          detail={error}
          onRetry={onRetry}
        />
      )
    }
    if (loading && jobs.length === 0) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 7 }}>
          <CircularProgress size={28} />
        </Box>
      )
    }
    if (rows.length === 0) {
      const copy = EMPTY_COPY[watchType][audience]
      return <EmptyState icon={<SensorsIcon />} title={copy.title} description={copy.description} />
    }

    if (isPhone) {
      return (
        <>
          <Stack spacing={1} sx={{ pb: 1 }}>
            {pagedRows.map((j) => (
              <Card key={j.id} variant="outlined">
                <CardActionArea onClick={() => onOpen(j)} sx={{ p: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {j.company}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {j.jobTitle}
                  </Typography>
                  <Box sx={{ mb: 1 }}>{renderEvent(j)}</Box>
                  <Stack spacing={0.75}>
                    <PhoneField label="Company LinkedIn">{renderLinkedIn(j)}</PhoneField>
                    <PhoneField label="Who held the role before">{renderHolder(j)}</PhoneField>
                    <PhoneField label="Date">
                      <Typography variant="body2">{renderDate(j)}</Typography>
                    </PhoneField>
                  </Stack>
                </CardActionArea>
                {/* Outside the CardActionArea: a control nested in one fires the
                    card's onClick as well, so changing the Action would also
                    open the detail dialog. */}
                <Box sx={{ px: 2, pb: 2 }}>
                  <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 0.5 }}>
                    Action
                  </Typography>
                  {renderAction(j)}
                </Box>
              </Card>
            ))}
          </Stack>
          <TablePaginationBar
            count={pageCtl.count}
            page={pageCtl.page}
            rowsPerPage={pageCtl.rowsPerPage}
            setPage={pageCtl.setPage}
            setRowsPerPage={pageCtl.setRowsPerPage}
          />
        </>
      )
    }

    return (
      <Paper variant="outlined" sx={tableStyles.tableContainer}>
        <Box sx={{ overflow: 'auto', maxHeight: '70vh' }}>
          <Table stickyHeader sx={{ ...tableStyles.table, ...(isCompact ? getCompactTableSx(theme) : undefined) }}>
            <TableHead>
              <TableRow>
                <SortableTableHeader id="company" label="Company" orderBy={orderBy} order={order} onSort={handleSort} sx={isCompact ? { width: '18%' } : undefined} />
                <SortableTableHeader id="event" label="Event" orderBy={orderBy} order={order} onSort={handleSort} />
                <SortableTableHeader id="role" label="Potential Open Role" orderBy={orderBy} order={order} onSort={handleSort} sx={isCompact ? { width: '18%' } : undefined} />
                {!isCompact && <TableCell sx={{ fontWeight: 600 }}>Company LinkedIn</TableCell>}
                {!isCompact && (
                  <SortableTableHeader id="holder" label="Who Held the Role Before" orderBy={orderBy} order={order} onSort={handleSort} />
                )}
                <SortableTableHeader id="date" label="Date" orderBy={orderBy} order={order} onSort={handleSort} sx={isCompact ? { width: 92 } : undefined} />
                <SortableTableHeader id="action" label="Action" orderBy={orderBy} order={order} onSort={handleSort} sx={isCompact ? { width: 170 } : { minWidth: 170 }} />
                {isCompact && <ExpanderHeaderCell />}
              </TableRow>
            </TableHead>
            <TableBody>
              {pagedRows.map((j) => (
                <Fragment key={j.id}>
                  <TableRow hover onClick={() => onOpen(j)} sx={{ cursor: 'pointer' }}>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {j.company}
                      </Typography>
                    </TableCell>
                    <TableCell>{renderEvent(j)}</TableCell>
                    <TableCell>
                      <Typography variant="body2">{j.jobTitle}</Typography>
                    </TableCell>
                    {!isCompact && <TableCell>{renderLinkedIn(j)}</TableCell>}
                    {!isCompact && <TableCell>{renderHolder(j)}</TableCell>}
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {renderDate(j)}
                      </Typography>
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>{renderAction(j)}</TableCell>
                    {isCompact && (
                      <ExpanderToggleCell
                        open={expandedRowId === j.id}
                        onToggle={() => setExpandedRowId(expandedRowId === j.id ? null : j.id)}
                        subject={`${j.jobTitle} at ${j.company}`}
                      />
                    )}
                  </TableRow>
                  {isCompact && (
                    <ExpanderRow open={expandedRowId === j.id} colSpan={COMPACT_COLUMN_COUNT}>
                      <ExpanderField label="Company LinkedIn">{renderLinkedIn(j)}</ExpanderField>
                      <ExpanderField label="Who Held the Role Before">{renderHolder(j)}</ExpanderField>
                    </ExpanderRow>
                  )}
                </Fragment>
              ))}
            </TableBody>
          </Table>
        </Box>
        <TablePaginationBar
          count={pageCtl.count}
          page={pageCtl.page}
          rowsPerPage={pageCtl.rowsPerPage}
          setPage={pageCtl.setPage}
          setRowsPerPage={pageCtl.setRowsPerPage}
        />
      </Paper>
    )
  }

  return (
    <Box>
      <Tabs
        value={watchType}
        onChange={(_, v) => {
          onWatchTypeChange(v as WatchType)
          setExpandedRowId(null)
        }}
        sx={{ borderBottom: 1, borderColor: 'divider', mb: 1 }}
      >
        {WATCH_TYPES.map((t) => (
          <Tab
            key={t}
            value={t}
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                {WATCH_TYPE_LABELS[t]}
                <Chip label={grouped[t].length} size="small" sx={{ height: 18, typography: 'badge' }} />
              </Box>
            }
          />
        ))}
      </Tabs>
      {renderBody()}
    </Box>
  )
}

function PhoneField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" component="div">
        {label}
      </Typography>
      {children}
    </Box>
  )
}
